import React, { useState } from 'react';
import { Card, Form, Input, Button, Upload, message, Space } from 'antd';
import type { UploadProps } from 'antd';
import { UploadOutlined } from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import { createLogisticsProvider } from '../../../services/logistics';

const { TextArea } = Input;

const AdminLogisticsCreate: React.FC = () => {
  const [form] = Form.useForm();
  const navigate = useNavigate();
  const [submitting, setSubmitting] = useState(false);
  const [logoFile, setLogoFile] = useState<File | null>(null);

  const uploadProps: UploadProps = {
    maxCount: 1,
    beforeUpload: (file) => {
      const isImage = file.type.startsWith('image/');
      if (!isImage) {
        message.error('只能上传图片文件');
        return Upload.LIST_IGNORE;
      }
      const isLt2M = file.size / 1024 / 1024 < 2;
      if (!isLt2M) {
        message.error('图片大小不能超过 2MB');
        return Upload.LIST_IGNORE;
      }
      setLogoFile(file);
      return false; // 阻止自动上传
    },
    onRemove: () => {
      setLogoFile(null);
    }
  };

  const onSubmit = async (values: any) => {
    setSubmitting(true);
    try {
      // 支持带文件的 multipart 提交
      const hasFile = !!logoFile;
      let payload: any;
      if (hasFile) {
        const fd = new FormData();
        fd.append('name', values.name.trim());
        fd.append('code', values.code.trim());
        if (values.website) fd.append('website', values.website.trim());
        if (values.remarks) fd.append('remarks', values.remarks.trim());
        if (logoFile) fd.append('logo', logoFile);
        payload = fd;
      } else {
        payload = {
          name: values.name?.trim(),
          code: values.code?.trim(),
          website: values.website?.trim() || '',
          remarks: values.remarks?.trim() || ''
        };
      }

      const resp = await createLogisticsProvider(payload);
      message.success('创建成功');
      // 返回管理员物流服务商列表
      navigate('/dashboard/admin/logistics/shipping-carriers');
    } catch (e: any) {
      const detail = e?.response?.data || e?.message || '创建失败';
      if (typeof detail === 'object') {
        Object.entries(detail).forEach(([k, v]) => message.error(`${k}: ${Array.isArray(v) ? v[0] : v}`));
      } else {
        message.error(detail);
      }
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="p-6">
      <Card title="创建物流服务商">
        <Form form={form} layout="vertical" onFinish={onSubmit} className="max-w-3xl">
          <Form.Item label="服务商名称" name="name" rules={[{ required: true, message: '请输入服务商名称' }]}> 
            <Input placeholder="如：顺丰速运" maxLength={100} />
          </Form.Item>

          <Form.Item label="服务商代码" name="code" rules={[{ required: true, message: '请输入服务商代码' }]}> 
            <Input placeholder="如：SF" maxLength={20} />
          </Form.Item>

          <Form.Item label="官方网站" name="website" rules={[{ type: 'url', message: '请输入正确的网址' }]}> 
            <Input placeholder="https://example.com" />
          </Form.Item>

          <Form.Item label="Logo 图片">
            <Upload {...uploadProps} listType="picture">
              <Button icon={<UploadOutlined />}>选择图片</Button>
            </Upload>
          </Form.Item>

          <Form.Item label="备注信息" name="remarks">
            <TextArea rows={4} maxLength={500} placeholder="补充说明（选填）" />
          </Form.Item>

          <Space>
            <Button onClick={() => navigate('/dashboard/admin/logistics/shipping-carriers')}>取消</Button>
            <Button type="primary" htmlType="submit" loading={submitting}>创建</Button>
          </Space>
        </Form>
      </Card>
    </div>
  );
};

export default AdminLogisticsCreate;


